import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { type Dict, type Lang, DEFAULT_LANG, getDict, pathForLang } from "./translations";
import { supabase } from "@/integrations/supabase/client";
import { mergeDict, type HomepageOverrides } from "@/lib/homepage-content";

type Ctx = {
  lang: Lang;
  t: Dict;
  switchPath: (target: Lang) => string;
};

const I18nContext = createContext<Ctx | null>(null);

async function fetchHomepageOverrides(lang: Lang): Promise<HomepageOverrides> {
  const { data, error } = await supabase
    .from("homepage_content")
    .select("section, content")
    .eq("lang", lang);
  if (error || !data) return {};
  const out: HomepageOverrides = {};
  for (const row of data) {
    (out as Record<string, unknown>)[row.section] = row.content;
  }
  return out;
}

export function I18nProvider({ lang, children }: { lang: Lang; children: ReactNode }) {
  const defaults = getDict(lang);
  const { data: overrides } = useQuery({
    queryKey: ["homepage-content", lang],
    queryFn: () => fetchHomepageOverrides(lang),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
  const t = useMemo(() => mergeDict(defaults, overrides ?? null), [defaults, overrides]);
  const switchPath = (target: Lang) => pathForLang(target);
  return <I18nContext.Provider value={{ lang, t, switchPath }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) return { lang: DEFAULT_LANG as Lang, t: getDict(DEFAULT_LANG), switchPath: pathForLang };
  return ctx;
}